import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {observer} from 'mobx-react'
import _ from 'lodash'
import {useInjection} from 'dna-react-ioc'
import {Input, MultiSelect, Select, Text} from '@/components'
import {useScreenType} from '@/hooks'
import {Alert, Checkbox, Col, Divider, Form, Radio, Row, Typography} from 'antd'
import {TInviteEditTeammateStore} from '@/pages/TeamManagement/components/InviteEditTeammate/TInviteEditTeammateStore'
import {InviteEditTeammateModalProps} from '@/pages/TeamManagement/components/InviteEditTeammate/props'
import {Role} from '@/stores/team-management/constants'
import {hasProducts} from '@/stores/auth/services'
import {ProductsMap} from '@/stores/auth/constants/products-map'
import {renderMobileSectionPermissionsFormItem, renderSectionPermissionsFormItem} from './services'
import {InviteTeammatePermissions} from './models'
import {AccessType} from './constants'
import styles from './styles.scss'
import {
    ADMIN_ROLE,
    FULL_ACCESS_RADIO_BUTTON,
    INVITE_AS_RADIO,
    NO_ACCESS_RADIO_BUTTON,
    READ_ACCESS_RADIO_BUTTON,
    RESTRICTED_ROLE,
    TEAMMATE_EMAIL,
    TEAMMATE_FIRST_NAME,
    TEAMMATE_LAST_NAME,
    TEAMMATE_ROLE_SELECT,
    TEAMMATE_ROLE_TYPE,
    TEAMMATES_STORES
} from '@/constants/playwright-ids'

export const InviteEditTeammate = observer(
    ({injectableIdentifier, formId, form, type}: InviteEditTeammateModalProps) => {
        const {t} = useTranslation()
        const {isMobile} = useScreenType()
        const {
            teammate,
            inviteEditTeammate,
            selectStores,
            isAdmin,
            setStores,
            stores,
            permissionSections,
            financePermissions,
            customPermissions,
            isSelfEdit
        } = useInjection<TInviteEditTeammateStore>(injectableIdentifier)

        const isTeammateAdmin = teammate?.role === Role.admin

        /**
         *  UI states
         */
        const [inviteAs, setInviteAs] = useState<AccessType>(
            teammate?.inviteAs || isAdmin ? AccessType.admin : AccessType.restricted
        )
        const [selectedRole, setSelectedRole] = useState<Role>(teammate?.role || Role.finance)
        const [selectedPermissions, setSelectedPermissions] = useState<InviteTeammatePermissions>(
            teammate?.permissions || financePermissions
        )

        useEffect(() => {
            const shopIds =
                teammate?.shopIds?.length > 0
                    ? Array.from(new Set(teammate?.shopIds))
                    : selectStores?.map((store) => store.value)

            form.setFieldValue('shopIds', shopIds)
            setStores(shopIds)
        }, [teammate, selectStores])

        useEffect(() => {
            form.resetFields(
                Object.keys(selectedPermissions).map(
                    (key) =>
                        ['permissions', key] as ['permissions', keyof typeof selectedPermissions]
                )
            )
        }, [selectedPermissions])

        useEffect(() => {
            form.resetFields(['role'])
        }, [selectedRole])

        useEffect(() => {
            form.resetFields(['inviteAs'])
        }, [inviteAs])

        useEffect(() => {
            teammate && setInviteAs(teammate.inviteAs)
            teammate && setSelectedRole(teammate.role)
            teammate && setSelectedPermissions(teammate.permissions)
            form.resetFields(['email', 'firstName', 'lastName'])
        }, [teammate])

        useEffect(() => {
            return () => {
                setInviteAs(
                    teammate?.inviteAs || isAdmin ? AccessType.admin : AccessType.restricted
                )
                setSelectedRole(teammate?.role || Role.finance)
                setSelectedPermissions(teammate && teammate.permissions)
            }
        }, [])

        const onInvitedAsEditChange = (inviteAs: AccessType) => {
            if (inviteAs === AccessType.restricted) {
                setSelectedRole(null)
            }

            setInviteAs(inviteAs)
        }

        const onFinish = (values) => {
            values.email = values.email?.toLowerCase()
            inviteEditTeammate(values).then(() => {
                setSelectedRole(Role.finance)
                setInviteAs(isAdmin ? AccessType.admin : AccessType.restricted)
                setSelectedPermissions(financePermissions)
                form.resetFields()
            })
        }

        const onPermissionChange = (sectionCode, permission) => {
            const _selectedPermissions = {...selectedPermissions, [sectionCode]: permission}
            setSelectedPermissions(_selectedPermissions)
            if (_.isEqual(_selectedPermissions, financePermissions)) {
                setSelectedRole(Role.finance)
            } else {
                setSelectedRole(Role.custom)
            }
        }

        const onRoleChange = (role) => {
            if (role === Role.finance) {
                setSelectedPermissions(financePermissions)
            } else {
                setSelectedPermissions(customPermissions)
            }
            setSelectedRole(role)
        }

        const renderDesktopPermissions = () => {
            return (
                <>
                    <Row>
                        <Col sm={10}>
                            <Text>{t('InviteEditTeammate.formFields.sectionName')}</Text>
                        </Col>
                        <Col sm={14}>
                            <Row>
                                <Col sm={8}>
                                    <Text type={'secondary'} data-test-id={NO_ACCESS_RADIO_BUTTON}>
                                        {t('InviteEditTeammate.formFields.noAccess')}
                                    </Text>
                                </Col>
                                <Col sm={8}>
                                    <Text
                                        type={'secondary'}
                                        data-test-id={READ_ACCESS_RADIO_BUTTON}
                                    >
                                        {t('InviteEditTeammate.formFields.readAccess')}
                                    </Text>
                                </Col>
                                <Col sm={8}>
                                    <Text
                                        type={'secondary'}
                                        data-test-id={FULL_ACCESS_RADIO_BUTTON}
                                    >
                                        {t('InviteEditTeammate.formFields.fullAccess')}
                                    </Text>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Divider className={styles.sectionPermissionsDivider} />
                    {permissionSections.map((section) => {
                        if (section.requiresAdmin && !isAdmin) return null
                        if (
                            section.requiresProduct &&
                            !hasProducts([ProductsMap[section.requiresProduct]])
                        )
                            return null

                        const accessLevels = section.accessLevels.map((value) => ({
                            value,
                            disabled: value === 'read' && section.readDisabled
                        }))

                        return renderSectionPermissionsFormItem({
                            sectionName: section.name,
                            sectionCode: section.code,
                            accessLevels,
                            onChange: onPermissionChange,
                            initialValue: selectedPermissions[section.code],
                            disabled: isSelfEdit || (section.requiresAdmin && !isAdmin)
                        })
                    })}
                </>
            )
        }

        const renderMobilePermissions = () => {
            return (
                <>
                    {permissionSections.map((section) => {
                        if (section.requiresAdmin && !isAdmin) return null
                        if (
                            section.requiresProduct &&
                            !hasProducts([ProductsMap[section.requiresProduct]])
                        )
                            return null

                        const accessLevels = section.accessLevels.map((value) => ({
                            label: t('InviteEditTeammate.formFields')[`${value}Access`],
                            value,
                            disabled: value === 'read' && section.readDisabled
                        }))

                        return renderMobileSectionPermissionsFormItem(
                            section.name,
                            section.code,
                            accessLevels,
                            onPermissionChange,
                            selectedPermissions[section.code],
                            isSelfEdit
                        )
                    })}
                </>
            )
        }

        const renderCreateUserAccessTypes = () => (
            <Radio.Group onChange={(event) => setInviteAs(event?.target?.value)}>
                {isAdmin && (
                    <Radio value={AccessType.admin}>
                        {t('InviteEditTeammate.formFields.inviteAsAdmin')}
                    </Radio>
                )}
                <Radio value={AccessType.restricted}>
                    {t('InviteEditTeammate.formFields.inviteAsRestrictedAccess')}
                </Radio>
            </Radio.Group>
        )

        const renderAdminAcknowledgement = () => {
            if (inviteAs === AccessType.admin) {
                return (
                    <Form.Item
                        name='adminAcknowledgement'
                        valuePropName='checked'
                        rules={[
                            {
                                validator: (_, value) =>
                                    value
                                        ? Promise.resolve()
                                        : Promise.reject(
                                              new Error(
                                                  t(
                                                      'InviteEditTeammate.formErrors.adminAcknowledgementRequired'
                                                  )
                                              )
                                          )
                            }
                        ]}
                    >
                        <Checkbox>{t('InviteEditTeammate.acknowledgement')}</Checkbox>
                    </Form.Item>
                )
            }
            return null
        }

        const renderEditUserAccessTypes = () => {
            return (
                <Radio.Group
                    disabled={isSelfEdit}
                    onChange={(event) => onInvitedAsEditChange(event?.target?.value)}
                >
                    {(isAdmin || (!isAdmin && isTeammateAdmin)) && (
                        <Radio value={AccessType.admin} data-test-id={ADMIN_ROLE}>
                            {t('InviteEditTeammate.formFields.inviteAsAdmin')}
                        </Radio>
                    )}
                    {(isAdmin || (!isAdmin && !isTeammateAdmin)) && (
                        <Radio value={AccessType.restricted} data-test-id={RESTRICTED_ROLE}>
                            {t('InviteEditTeammate.formFields.inviteAsRestrictedAccess')}
                        </Radio>
                    )}
                </Radio.Group>
            )
        }

        return (
            <Form
                id={formId}
                form={form}
                layout={'vertical'}
                onFinish={onFinish}
                autoComplete='off'
            >
                <Form.Item
                    label={t('InviteEditTeammate.formFields.email')}
                    name={'email'}
                    initialValue={teammate?.email}
                    rules={[
                        {required: true, message: t('InviteEditTeammate.formErrors.emailRequired')},
                        {type: 'email', message: t('InviteEditTeammate.formErrors.enterValidEmail')}
                    ]}
                >
                    <Input
                        disabled={!!teammate || isSelfEdit}
                        placeholder={t('InviteEditTeammate.formFields.email')}
                        data-test-id={TEAMMATE_EMAIL}
                    />
                </Form.Item>
                <Form.Item
                    label={t('InviteEditTeammate.formFields.firstName')}
                    name={'firstName'}
                    initialValue={teammate?.firstName}
                    rules={[
                        {
                            required: true,
                            message: t('InviteEditTeammate.formErrors.firstNameRequired')
                        }
                    ]}
                >
                    <Input
                        placeholder={t('InviteEditTeammate.formFields.firstName')}
                        data-test-id={TEAMMATE_FIRST_NAME}
                    />
                </Form.Item>
                <Form.Item
                    label={t('InviteEditTeammate.formFields.lastName')}
                    name={'lastName'}
                    initialValue={teammate?.lastName}
                    rules={[
                        {
                            required: true,
                            message: t('InviteEditTeammate.formErrors.lastNameRequired')
                        }
                    ]}
                >
                    <Input
                        placeholder={t('InviteEditTeammate.formFields.lastName')}
                        data-test-id={TEAMMATE_LAST_NAME}
                    />
                </Form.Item>
                <Form.Item
                    name={'shopIds'}
                    label={t('InviteEditTeammate.formFields.store')}
                    rules={[
                        {required: true, message: t('InviteEditTeammate.formErrors.shopRequired')}
                    ]}
                    initialValue={stores}
                    data-test-id={TEAMMATES_STORES}
                    getValueProps={(value) => ({value: value ?? stores})}
                    getValueFromEvent={(v) => {
                        const allStoreIds = selectStores?.map((store) => store.value) || []
                        const valueToSet = Array.isArray(v) ? v : allStoreIds

                        setStores(valueToSet)
                        form.setFieldValue('shopIds', valueToSet)
                        return valueToSet
                    }}
                >
                    <MultiSelect
                        disabled={isSelfEdit}
                        isSearchable={true}
                        filterOption={(input, option) =>
                            String(option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        placeholder={t('InviteEditTeammate.formFields.store')}
                        options={selectStores}
                    />
                </Form.Item>
                <Typography.Title level={5}>
                    {t('InviteEditTeammate.formFields.teammatePermissions')}
                </Typography.Title>
                {isSelfEdit && (
                    <Alert
                        type='info'
                        showIcon
                        message={t('InviteEditTeammate.formFields.cannotEditOwnField')}
                        className={styles.selfEditAlert}
                    />
                )}
                <Form.Item
                    name={'inviteAs'}
                    initialValue={inviteAs}
                    rules={[
                        {
                            required: true,
                            message: t('InviteEditTeammate.formErrors.lastNameRequired')
                        }
                    ]}
                    data-test-id={INVITE_AS_RADIO}
                >
                    {type === 'create'
                        ? renderCreateUserAccessTypes()
                        : renderEditUserAccessTypes()}
                </Form.Item>
                {renderAdminAcknowledgement()}
                {inviteAs === AccessType.restricted && (
                    <>
                        <Form.Item
                            name={'role'}
                            initialValue={selectedRole}
                            rules={[
                                {
                                    required: true,
                                    message: t('InviteEditTeammate.formErrors.roleRequired')
                                }
                            ]}
                            data-test-id={TEAMMATE_ROLE_SELECT}
                        >
                            <Select
                                disabled={isSelfEdit}
                                placeholder={t('InviteEditTeammate.formFieldsPlaceholders.role')}
                                options={[
                                    {
                                        label: t('InviteEditTeammate.formFields.accountantAccess'),
                                        value: 'finance'
                                    },
                                    {
                                        label: t('InviteEditTeammate.formFields.customAccess'),
                                        value: 'custom'
                                    }
                                ]}
                                onChange={onRoleChange}
                                data-test-id={TEAMMATE_ROLE_TYPE}
                            />
                        </Form.Item>

                        <Typography.Title level={5}>
                            {t('InviteEditTeammate.formFields.grantedPermissions')}
                        </Typography.Title>
                        {!isMobile && renderDesktopPermissions()}
                        {isMobile && renderMobilePermissions()}
                    </>
                )}
            </Form>
        )
    }
)
